import { useEffect, useRef, type ReactNode } from 'react';
import { X, Printer } from 'lucide-react';

/**
 * ModalShell — boilerplate modal réutilisable (focus trap + Escape + body lock + restore focus).
 * Pattern cross-Intralys adapté à la palette cream + terracotta + Garamond Océanne.
 * Réutilisé par CarnetModal, LexiqueModal, ColophonModal.
 *
 * Features :
 * - Focus trap (WCAG 2.4.3)
 * - Escape ferme + restore focus au trigger
 * - Body scroll lock pendant ouverture
 * - Backdrop click ferme
 * - Bouton Imprimer optionnel (printable=true)
 */

interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  eyebrow: string;
  title: string;
  closeLabel: string;
  ariaLabelledById: string;
  children: ReactNode;
  /** Largeur max desktop. Défaut max-w-3xl. */
  maxWidth?: string;
  /** Si true, affiche un bouton Imprimer dans le header. */
  printable?: boolean;
  /** Label du bouton Imprimer (i18n). */
  printLabel?: string;
}

export function ModalShell({
  isOpen,
  onClose,
  eyebrow,
  title,
  closeLabel,
  ariaLabelledById,
  children,
  maxWidth = 'max-w-3xl',
  printable = false,
  printLabel = 'Imprimer'
}: ModalShellProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';

    const FOCUSABLE_SELECTOR =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const container = containerRef.current;
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('inert'));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    closeBtnRef.current?.focus();

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledById}
      // data-lenis-prevent : neutralise le smooth scroll Lenis sur tout le modal
      // (backdrop + conteneur). Sans ça, la molette défilait la page derrière au
      // lieu du contenu scrollable du modal (lexique/carnet/colophon).
      data-lenis-prevent
      className="fixed inset-0 z-[70] flex items-center justify-center p-0 md:p-6 bg-foreground/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        className={`relative bg-background w-full ${maxWidth} max-h-[100vh] md:max-h-[88vh] overflow-y-auto shadow-2xl rounded-none md:rounded-lg`}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 md:px-10 py-5 bg-background border-b border-border/60">
          <div className="min-w-0">
            <p className="mb-1 font-display text-sm italic tracking-wide text-secondary">{eyebrow}</p>
            <h2
              id={ariaLabelledById}
              className="font-display text-xl md:text-2xl leading-tight text-balance text-foreground"
            >
              {title}
            </h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {printable && (
              <button
                type="button"
                onClick={() => window.print()}
                aria-label={printLabel}
                title={printLabel}
                className="p-2 text-foreground/70 hover:text-foreground transition-colors"
              >
                <Printer size={20} aria-hidden="true" />
              </button>
            )}
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="p-2 -mr-2 text-foreground hover:text-foreground/70 transition-colors"
            >
              <X size={24} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Body scrollable */}
        <div className="px-6 md:px-10 py-8">{children}</div>
      </div>
    </div>
  );
}
